import { alpha, Backdrop, Box, Card, CardHeader, CircularProgress, Divider, Grid, Stack, Tab, TableBody, TableCell, TableHead, TableRow, Typography } from "@mui/material";
import { TabContext, TabList, TabPanel } from "@mui/lab";
import { FC, ReactNode, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { push } from "connected-react-router";
import { ListContextProvider, ListControllerProps, ListPaginationContext, useListContext, useListController } from "ra-core";
import { FileRefDto, PrimaryClassification } from "@/gen/api/securefile";
import { Pagination as DefaultPagination } from '@/components/list/pagination';
import Scrollbar from "@/components/Scrollbar";
import { Content } from "@/components/content/Content";
import { useResourceRegister } from "@/utils/resourceRegister";
import { DatagridRoot, datagridClasses as classes } from "@/components/list/datagrid/useDatagridStyles";
import classnames from "classnames";
import bytesToSize from "@/utils/bytesToSize";
import numeral from "numeral";
import { OdResourcePageProps } from "@/types/resource";
import { PopTimeRangeKind } from "@/components/input/PopTimeRangePicker";
import { useUserTimeFormat } from "@/utils/userTimeFormat";
import { usePickPaginationContext } from "ra-core/esm/controller/ListPaginationContext";
import { getUserProfile } from "@/reducers/userProfile";
import { base64UrlDecode } from "@/utils/base64";


const res_r = 'secure-files/dm-report-file-refs-r'
const res_0 = 'secure-files/dm-report-file-refs-0'
const res_1 = 'secure-files/dm-report-file-refs-1'


const DMReportRefFiles: FC<OdResourcePageProps> = (props) => {
  useResourceRegister({ name: res_r })
  useResourceRegister({ name: res_0 })
  useResourceRegister({ name: res_1 })

  const dispatch = useDispatch()
  const location = useLocation()
  const userProfile = useSelector(getUserProfile)
  const [states, setStates] = useState({
    baseParam: extractParam(location.search, userProfile?.ianaTimeZone ?? null)
  })
  useEffect(() => {
    const baseParam = extractParam(location.search, userProfile?.ianaTimeZone ?? null)
    setStates(v => 
      JSON.stringify(v.baseParam) !== JSON.stringify(baseParam)
      ? {...v, baseParam} : v)
    if (!baseParam) {
      dispatch(push('/secure-files/data-manager-reports'))
    }
  }, [dispatch, location.search, userProfile?.ianaTimeZone])

  if (!states.baseParam) { return null }

  return (
    <DMReportRefFilesBody {...props} refFilesParams={states.baseParam} />
  )
}

function extractParam(search: string|null, userTimezone: string|null) {
  if (!search) { return null }
  const ps = new URLSearchParams(search);
  try {
    const s = ps.get('RefFilesParams')
    const refFilesParams: RefFilesParams = JSON.parse(
      s ? base64UrlDecode(s) : '{}');
    refFilesParams.notAfter = refFilesParams.notAfter ?? new Date().toISOString()
    refFilesParams.ianaTimeZone = userTimezone
    const invalid = !refFilesParams || !refFilesParams.notBefore ||
      (
        refFilesParams.inReport == null &&
        refFilesParams.protectedAtDate == null &&
        refFilesParams.protectedAtWeekOf == null &&
        refFilesParams.classifiedAsPClss == null &&
        refFilesParams.classifiedAsSClss == null);
    return invalid ? null : refFilesParams
  } catch {
    return null
  }
}

const DMReportRefFilesBody: FC<OdResourcePageProps&{refFilesParams: RefFilesParams}> = ({
  refFilesParams: {timerangeDesc, ...filter}, options,
}) => {
  const currRes = 
    filter.inReport ? [res_r]
    : (filter.protectedAtDate || filter.protectedAtWeekOf) ? [res_0]
    : (filter.classifiedAsPClss != null || filter.classifiedAsSClss) ? [res_0, res_1]
    : [res_r]
  
  const listCtrl0 = useListController({ resource: currRes[0], basePath: '', perPage: 25, filterDefaultValues: {...filter, kind: 0 } })
  const listCtrl1 = useListController({ resource: currRes[1] ?? 'noop', basePath: '', perPage: 25, filterDefaultValues: {...filter, kind: 1 } })
  const [states, setStates] = useState({
    tab: 'encrypted' as 'encrypted'|'unencrypted'
  });

  const handleTabChange = (event: any, newVal: 'encrypted'|'unencrypted') => {
    setStates(v => ({...v, tab: newVal }))
  }
  const format = useUserTimeFormat()
  const tdisplay = timeDisplayOnParams(format)
  const [label1, label2] = 
      filter.inReport ? ['Specified Report', timerangeDesc] as [ReactNode, ReactNode]
    : filter.protectedAtDate ? ['Protected Files at Date', format(filter.protectedAtDate || 0, 'PP')]
    : filter.protectedAtWeekOf ? ['Protected Files at Week of Date', format(filter.protectedAtWeekOf || 0, 'PP')]
    : filter.classifiedAsPClss ? [<>Files Classified as <Typography fontWeight="300" component="span" sx={{ml:2}}>{PrimaryClassification[filter.classifiedAsPClss]}</Typography></>, tdisplay(filter as any)]
    : filter.classifiedAsSClss ? [<>Files Classified as <Typography fontWeight="300" component="span" sx={{ml:2}}>{timerangeDesc}</Typography></>, tdisplay(filter as any)]
    : ['', '']

  return (
    <Content 
      pageName="Scan Report Related Files" 
      resourceName="Scan Report Related Files" 
      breadcrumbs={[ ...(options?.breadcrumbs ?? []), { 
        label: 'Data Manager Reports', 
        path: `/secure-files/data-manager-reports`, 
        locationDescriptor: { pathname: '/secure-files/data-manager-reports', state: filter }
      } ]}
    >
      <Grid container spacing={4}>
        <Grid item xs={12}>
          <Card>
            <Stack direction="row" spacing={4} alignItems="center" sx={{ m: 2 }}>
              <Typography variant="h6">{label1}</Typography>
              <Box>{label2}</Box>
            </Stack>
          </Card>
        </Grid>
        <Grid item xs={12}>
          <Card>
            <CardHeader title="Scanned Files" />
            <Divider />
            {currRes.length === 1 &&
              <ListContextProvider value={listCtrl0}>
                <PageHintIngectionListPaginationContextProvider filter={filter} kind={0} >
                  <TableInTab />
                </PageHintIngectionListPaginationContextProvider>
              </ListContextProvider>}
            {currRes.length === 2 &&
              <Box sx={{
                '.MuiTabPanel-root': { px: 0, pt: 1, pb: 0 }
              }}>
                <TabContext value={states.tab}>
                  <Box sx={{ borderBottom: theme => `1px solid ${theme.palette.divider}` }}>
                    <TabList onChange={handleTabChange}>
                      <Tab label="Encrypted" value="encrypted" />
                      <Tab label="Unencrypted" value="unencrypted" />
                    </TabList>
                  </Box>
                  <TabPanel value="encrypted">
                    <ListContextProvider value={listCtrl0}>
                      <PageHintIngectionListPaginationContextProvider filter={filter} kind={0} >
                        <TableInTab />
                      </PageHintIngectionListPaginationContextProvider>
                    </ListContextProvider>
                  </TabPanel>
                  <TabPanel value="unencrypted">
                    <ListContextProvider value={listCtrl1}>
                      <PageHintIngectionListPaginationContextProvider filter={filter} kind={1} >
                        <TableInTab />
                      </PageHintIngectionListPaginationContextProvider>
                    </ListContextProvider>
                  </TabPanel>
                </TabContext>
              </Box>}
          </Card>
          <Backdrop open={listCtrl0.loading || listCtrl1.loading} sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}>
            <CircularProgress />
          </Backdrop>
        </Grid>
      </Grid>
    </Content>
  )
}

const buildPagehint = (listCtrl: ListControllerProps<any>|null) => {
  if (!listCtrl || !listCtrl.ids || !listCtrl.data || listCtrl.page < 2) { return '' }
  const id = (listCtrl.ids ?? [])[0]
  if (!id) { return '' }
  const dto : FileRefDto|undefined = listCtrl.data[id] as any
  if (!dto) { return '' }
  return `${dto.reportId},${listCtrl.total},${listCtrl.page}`
}

const THeadTitle = (s: string, width?: number) => {
  return (
    <TableCell key={s}
      className={classnames(classes?.headerCell)}
      variant="head"
      {...(width != null && { width: `${width}px`})}
    >
      <span>{s}</span>
    </TableCell>
  )
}
const DirRow : FC<{x: DirDto}> = ({x}) => {
  return (
    <TableRow className={classnames('dir-row', classes?.row, classes?.row)}>
      <TableCell className={classnames(classes.rowCell)}>
        <Typography component="span" variant="subtitle2">{x.computer}</Typography>
      </TableCell>
      <TableCell className={classnames(classes.rowCell)} colSpan={2}>
        <Typography component="span" variant="body2"><i>{x.path}</i></Typography>
      </TableCell>
    </TableRow>
  )
}
const FileRefRow : FC<{x: FileRefDto}> = ({x}) => {
  let sizeDisplayText = bytesToSize(x.size ?? 0)
  if (sizeDisplayText !== '') {
    sizeDisplayText = sizeDisplayText.toUpperCase().replace(/\d+/, (m) => numeral(m).format('0,0'))
  }
  return (
    <TableRow className={classnames('file-ref-row', classes?.row, classes?.row)}>
      <TableCell className={classnames(classes.rowCell)} />
      <TableCell className={classnames(classes.rowCell, 'filename')}>
        <Typography variant="body2" component="span">{(x as any).filename}</Typography>
      </TableCell>
      <TableCell className={classnames(classes.rowCell)}>
        <Typography variant="body2" component="span">{sizeDisplayText}</Typography>
      </TableCell>
    </TableRow>
  )
}
const EmptyRefRow : FC<{x: FileRefDto}> = ({x}) => {
  return (
    <TableRow className={classnames('empty-ref-row', classes?.row, classes?.row)}>
      <TableCell className={classnames(classes.rowCell)}>
        <Typography component="span" variant="subtitle2">{x.machine}</Typography>
      </TableCell>
      <TableCell className={classnames(classes.rowCell, 'empty-filename')} colSpan={2}>
        <Typography variant="body2" component="span" color="warning.light">File record has been removed</Typography>
      </TableCell>
    </TableRow>
  )
}
function isDir (x: DirDto|FileRefDto): x is DirDto {
  return 'isDir' in x
}

const TableInTab: FC = () => {
  const { ids, data, } = useListContext()
  const ls: FileRefDto[] = ids.map(id => data[id]) as any
  const {ls: ls1} = ls.sort(refDtoSorter).reduce((acc, x) => {
    if (x.path) {
      const p = (x.path ?? '')
      const i = Math.max(p.lastIndexOf('\\'), p.lastIndexOf('/'))
      const dir = p.substring(0, i)
      const filename = p.substring(i + 1);
      (x as any).filename = filename
      if (dir !== acc.curr || x.machine !== acc.currM) {
        acc.ls.push({ isDir: true, computer: x.machine!, path: dir })
        acc.curr = dir
        acc.currM = x.machine!
      }
    }
    acc.ls.push(x)
    return acc
  }, {curr: '', currM: '', ls: [] as (FileRefDto|DirDto)[]})

  return (
    <>
      <Scrollbar options={{ suppressScrollY: true }}>
        <Box sx={{ minWidth: 1150, position: 'relative' }}>
          <DatagridRoot
            className={classnames(classes.table)}
            size={'small'}
            sx={{ 
              '& tr.dir-row': {
                bgcolor: theme => alpha(theme.palette.success.light, 0.05),
                '& td': { py: 0.125 },
              },
              '& tr.file-ref-row': {
                '& td.filename': { textAlign: 'right' },
              },
              '& tr.empty-ref-row': {
                bgcolor: theme => alpha(theme.palette.divider, 0.02),
              },
            }}
          >
            <TableHead className={classnames(classes?.thead)}>
              <TableRow className={classnames(classes?.row, classes?.headerRow)}>
                {[
                  THeadTitle('Computer', 160),
                  THeadTitle('Location'),
                  THeadTitle('Size', 280)
                ]}
              </TableRow>
            </TableHead>
            <TableBody className={classnames('datagrid-body', classes.tbody)} >
              {ls1.map((x, i) =>
                isDir(x) ? <DirRow key={i} x={x} />
                : x.path ? <FileRefRow key={i} x={x} />
                : <EmptyRefRow key={i} x={x} />
              )}
            </TableBody>
          </DatagridRoot>
        </Box>
      </Scrollbar>
      {//@ts-ignore
        <DefaultPagination rowsPerPageOptions={[25, 50, 100]} />}
    </>
  )
}

const refDtoSorter = (a: FileRefDto, b: FileRefDto) => {
  return !a.machine ? 1
    : !b.machine ? -1
    : a.machine! === b.machine! 
      ? !a.path ? 1
        : !b.path ? -1
        : (a.path! < b.path! ? -1 : 1)
    : a.machine! < b.machine! ? -1 : 1
}

export default DMReportRefFiles;

interface RefFilesParams {
  notBefore: string,
  notAfter: string,
  trKind: PopTimeRangeKind,
  clients?: string[],
  clientNames?: string[],
  timerangeDesc?: string|null,
  inReport?: number|null,
  protectedAtDate?: string|null,
  protectedAtWeekOf?: string|null,
  ianaTimeZone?: string|null,
  classifiedAsPClss?: PrimaryClassification|null,
  classifiedAsSClss?: number|null,
}

interface DirDto {
  computer: string;
  path: string;
  isDir: true;
}

interface UserTimeFormat {
  (date: string | Date | number, formatStr: string, options?: any) : string
}

const timeDisplayOnParams = (format: UserTimeFormat) => (x: RefFilesParams) => {
  return (
      x.trKind === PopTimeRangeKind.Hours_24 ? 'Last 24 hours'
    : x.trKind === PopTimeRangeKind.Days_7 ? 'Last 7 days'
    : x.trKind === PopTimeRangeKind.Days_30 ? 'Last 30 days'
    : x.trKind === PopTimeRangeKind.Months_3 ? 'Last 3 months'
    : x.trKind === PopTimeRangeKind.Months_6 ? 'Last 6 months'
    : x.trKind === PopTimeRangeKind.Years_1 ? 'Last 1 year'
    : `From ${format(x.notBefore, 'PPp')}  To ${format(x.notAfter, 'PPp')}`);
}

const PageHintIngectionListPaginationContextProvider : FC<{filter:any, kind:any}> = ({ filter, kind, children }) => {
  const ctrls = useListContext()
  const { loaded, loading, displayedFilters, page, setFilters, setPage: originalSetPage } = ctrls
  const [states, setStates] = useState({
    currPage: 1,
    pageHint: '',
  })
  useEffect(() => {
    if (loaded) {
      setStates({ currPage: page, pageHint: buildPagehint(ctrls) })
    }
  }, [ctrls, loaded, page])

  const setPage = (x: number) => {
    setFilters({...filter, kind, pageHint: states.pageHint}, displayedFilters, false)
    originalSetPage(x)
  }
  
  return (
    <ListPaginationContext.Provider
        value={usePickPaginationContext({...ctrls, setPage})}
    >
        {children}
    </ListPaginationContext.Provider>
  )
}